/**
 * Shared Firebase configuration helpers.
 * Pure functions — no Firebase SDK dependency.
 * Each app passes its own env vars and calls Firebase SDK itself.
 */

/** Canonical project ID used when running against the emulator. */
export const EMULATOR_PROJECT_ID = 'staging-10seat';

/** Standard emulator ports used across the monorepo. */
export const EMULATOR_PORTS = {
  auth: 9098,
  firestore: 8082,
  functions: 5002,
  storage: 9199,
} as const;

export interface EmulatorEnv {
  isDev: boolean;
  useEmulator: boolean;
  isLocalhost?: boolean;
}

/**
 * Determine whether to connect to Firebase emulators.
 * Canonical rule: DEV mode AND (explicit flag OR running on localhost).
 */
export function shouldUseEmulator(env: EmulatorEnv): boolean {
  return env.isDev && (env.useEmulator || !!env.isLocalhost);
}

export interface FirebaseEnvVars {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
}

/**
 * Build the Firebase config object from Vite environment variables.
 * Overrides projectId to EMULATOR_PROJECT_ID when in emulator mode.
 */
export function buildFirebaseConfig(
  envVars: FirebaseEnvVars,
  emulatorEnv?: EmulatorEnv,
): Record<string, string> {
  const useEmu = emulatorEnv ? shouldUseEmulator(emulatorEnv) : false;
  const config: Record<string, string> = {
    apiKey: envVars.apiKey ?? '',
    authDomain: envVars.authDomain ?? '',
    projectId: useEmu ? EMULATOR_PROJECT_ID : (envVars.projectId ?? ''),
    storageBucket: envVars.storageBucket ?? '',
    messagingSenderId: envVars.messagingSenderId ?? '',
    appId: envVars.appId ?? '',
  };
  if (envVars.measurementId) {
    config.measurementId = envVars.measurementId;
  }
  return config;
}
